import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Target, Eye, Crosshair, Users, Shield, Handshake, Clock, Lightbulb } from "lucide-react";
import { useCmsText } from "@/hooks/useCmsText";
import { safeHtml } from "@/lib/safeHtml";
import marisaGranada from "@/assets/marisa-granada.webp";
import hodaFakhro from "@/assets/hoda-fakhro.webp";

export function ApproachSection() {
  const { t } = useTranslation();
  const cms = useCmsText("landing");

  const features = [
    { key: "strategic", icon: Crosshair },
    { key: "experienced", icon: Target },
    { key: "marketClose", icon: Eye },
    { key: "international", icon: Users },
    { key: "responsible", icon: Shield },
    { key: "partnership", icon: Handshake },
    { key: "longterm", icon: Clock },
  ];

  return (
    <section id="ansatz" className="py-24 md:py-32 lg:py-40 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-24"
          >
            <span className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground border-l-2 border-mh-cyan pl-4 mb-8">
              {cms("about.badge", t("approach.about.badge"))}
            </span>
            <h2 className="text-2xl md:text-3xl font-display text-foreground mb-10 leading-relaxed max-w-3xl">
              {cms("about.intro", t("approach.about.intro"))}
            </h2>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed max-w-3xl">
              <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("about.text1", t("approach.about.text1"))) }} />
              <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("about.text2", t("approach.about.text2"))) }} />
              <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("about.text3", t("approach.about.text3"))) }} />
            </div>
          </motion.div>

          {/* Decorative image - Full width, editorial */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative mb-24 -mx-6 lg:-mx-8"
          >
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 justify-center items-center px-6 lg:px-8">
              <div className="flex flex-col items-center text-center">
                <img
                  loading="lazy" decoding="async"
                  src={marisaGranada} 
                  width={900}
                  height={1350}
                  alt={`Marisa Granada – ${t("approach.founders.role")}, M&H Global Market GmbH`}
                  className="w-48 h-48 md:w-56 md:h-56 object-cover object-top rounded-full shadow-lg"
                />
                <h3 className="mt-4 text-lg font-display text-foreground">Marisa Granada</h3>
                <p className="text-sm text-muted-foreground">{t("approach.founders.role")}</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <img
                  loading="lazy" decoding="async"
                  src={hodaFakhro} 
                  width={900}
                  height={900}
                  alt={`Hoda Fakhro – ${t("approach.founders.role")}, M&H Global Market GmbH`}
                  className="w-48 h-48 md:w-56 md:h-56 object-cover object-top rounded-full shadow-lg"
                />
                <h3 className="mt-4 text-lg font-display text-foreground">Hoda Fakhro</h3>
                <p className="text-sm text-muted-foreground">{t("approach.founders.role")}</p>
              </div>
            </div>
          </motion.div>

          {/* Vision & Mission Grid */}
          <div className="grid md:grid-cols-2 gap-16 mb-24">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[hsl(185,65%,18%)]/10 flex items-center justify-center">
                  <Lightbulb className="h-6 w-6 text-[hsl(185,65%,18%)]" />
                </div>
                <h3 className="text-2xl font-display text-foreground">{cms("vision.title", t("approach.vision.title"))}</h3>
              </div>
              <p className="text-lg text-[hsl(38,65%,45%)] font-medium mb-4">
                <span dangerouslySetInnerHTML={{ __html: safeHtml(cms("vision.text1", t("approach.vision.text1"))) }} />
              </p>
              <div className="space-y-4 text-muted-foreground">
                <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("vision.text2", t("approach.vision.text2"))) }} />
                <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("vision.text3", t("approach.vision.text3"))) }} />
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[hsl(185,65%,18%)]/10 flex items-center justify-center">
                  <Target className="h-6 w-6 text-[hsl(185,65%,18%)]" />
                </div>
                <h3 className="text-2xl font-display text-foreground">{cms("mission.title", t("approach.mission.title"))}</h3>
              </div>
              <div className="space-y-4 text-muted-foreground">
                <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("mission.text1", t("approach.mission.text1"))) }} />
                <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("mission.text2", t("approach.mission.text2"))) }} />
                <p dangerouslySetInnerHTML={{ __html: safeHtml(cms("mission.text3", t("approach.mission.text3"))) }} />
                <p className="font-medium text-foreground" dangerouslySetInnerHTML={{ __html: safeHtml(cms("mission.text4", t("approach.mission.text4"))) }} />
              </div>
            </motion.div>
          </div>

          {/* Approach Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground border-l-2 border-mh-cyan pl-4 mb-8">
              {cms("approach.badge", t("approach.badge"))}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display mb-6 text-foreground">
              {cms("approach.title", t("approach.title"))} <span className="text-[hsl(180,60%,40%)]">{cms("approach.subtitle", t("approach.subtitle"))}</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
              <span dangerouslySetInnerHTML={{ __html: safeHtml(cms("approach.description", t("approach.description"))) }} />
            </p>
            <div className="text-muted-foreground mb-2">{cms("approach.moduleIntro", t("approach.moduleIntro"))}</div>
            <div className="text-xl font-display text-[hsl(185,65%,18%)]">{cms("approach.moduleSteps", t("approach.moduleSteps"))}</div>
          </motion.div>

          {/* Features - Clean horizontal list */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="border-t border-border pt-12"
          >
            <h3 className="text-sm font-medium tracking-[0.15em] uppercase text-muted-foreground mb-8 text-center">
              {cms("approach.featuresTitle", t("approach.featuresTitle"))}
            </h3>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.05 * index }}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon className="h-4 w-4 text-[hsl(180,60%,40%)]" />
                    <span>{t(`approach.features.${feature.key}.title`)}</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
